import type { AssetItem } from '$/api/server.types';

/**
 * Masonry 卡片列宽下限（px）。
 * 过低时宽屏会排过多列、单卡过窄；240 在常见桌面宽度下列数更少、单卡更宽。
 */
export const MASONRY_CARD_MIN_WIDTH = 240;
/** Masonry 卡片列宽上限（px）；过宽时加列拆分，与主站瀑布流逻辑一致 */
export const MASONRY_CARD_MAX_WIDTH = 300;
/** 画布资产弹窗内 Masonry 单列固定宽度（px）；与 AssetModalContent `.masonryColumn` 一致 */
export const ASSET_MODAL_MASONRY_CARD_WIDTH = 160;
/** 与 `ASSET_MODAL_MASONRY_CARD_WIDTH` 相同（旧名保留供套件外引用） */
export const ASSET_MODAL_MASONRY_CARD_MIN_WIDTH = ASSET_MODAL_MASONRY_CARD_WIDTH;
/** 与 `ASSET_MODAL_MASONRY_CARD_WIDTH` 相同（旧名保留供套件外引用） */
export const ASSET_MODAL_MASONRY_CARD_MAX_WIDTH = ASSET_MODAL_MASONRY_CARD_WIDTH;

/** Masonry 列间距（px） */
export const MASONRY_GAP_PX = 8;
/**
 * Masonry 最大列数（与 `apps/roboneo-web` Projects / CreativeCaseList 列表 cap=24 对齐，避免超宽屏单列过宽）。
 */
export const MASONRY_MAX_COLUMNS = 24;

/** Figma 33703:123959：文档卡近似宽高比（宽度 200、内容区高度约 104） */
export const DOCUMENT_CARD_ASPECT_RATIO = 200 / 104;

/** 音频素材卡片封面固定边长（px）；与列宽取 min，窄列时随列宽缩放仍保持 1:1 */
export const ASSET_AUDIO_CARD_DISPLAY_PX = 160;

/**
 * 瀑布流列高累计（以「列内容宽度 = 1」的相对高度）：音频卡宽为 min(160, 列宽)、1:1 方形。
 *
 * @param columnContentWidthPx 单列内容宽度（px）
 */
export function masonryAudioItemHeightShare(columnContentWidthPx: number): number {
  const w = Number(columnContentWidthPx);
  if (!Number.isFinite(w) || w <= 0) return 1;
  const cardW = Math.min(ASSET_AUDIO_CARD_DISPLAY_PX, w);
  return cardW / w;
}

const IMAGE_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp']);
const VIDEO_EXT = new Set(['mp4', 'mov']);
const AUDIO_EXT = new Set(['mp3', 'wav']);
const PPT_EXT = new Set(['ppt', 'pptx']);
const EXCEL_EXT = new Set(['xlsx', 'xls']);
const WORD_EXT = new Set(['doc', 'docx']);

/**
 * 资产卡片展示维度的文件类型（由 URL 后缀与 material_type 共同判定）。
 */
export type AssetCardVariant =
  | 'image'
  | 'video'
  | 'audio'
  | 'ppt'
  | 'excel'
  | 'pdf'
  | 'word'
  | 'markdown'
  | 'text'
  | 'csv'
  | 'unknown';

type DocumentCardVisual = {
  icon: string;
  iconBg: string;
  typeLabel: string;
};

/**
 * 按容器宽度计算 masonry 列数：算法与主站 `Projects` / `CreativeCaseList` / `Waterfall` 一致——
 * 先按最小列宽尽可能多列，若均分列宽仍大于 `MASONRY_CARD_MAX_WIDTH` 则继续加列，直至列宽落入区间或达到 `MASONRY_MAX_COLUMNS`。
 *
 * @param containerWidthPx 容器内容宽度（px）
 * @param options 可选；覆盖默认 `MASONRY_CARD_MIN_WIDTH` / `MASONRY_CARD_MAX_WIDTH`
 */
export type MasonryColumnCountOptions = {
  minCardWidth?: number;
  maxCardWidth?: number;
};

export function masonryColumnCountForWidth(
  containerWidthPx: number,
  options?: MasonryColumnCountOptions,
): number {
  const w = Math.max(0, containerWidthPx);
  if (w <= 0) return 1;
  const gap = MASONRY_GAP_PX;
  const minW = options?.minCardWidth ?? MASONRY_CARD_MIN_WIDTH;
  const maxW = options?.maxCardWidth ?? MASONRY_CARD_MAX_WIDTH;
  const maxCols = MASONRY_MAX_COLUMNS;

  let cols = Math.max(1, Math.floor((w + gap) / (minW + gap)));
  let itemWidth = Math.floor((w - gap * (cols - 1)) / cols);

  while (itemWidth > maxW) {
    const nextCols = cols + 1;
    const nextItemWidth = Math.floor((w - gap * (nextCols - 1)) / nextCols);
    if (nextItemWidth < minW || nextCols > maxCols) break;
    cols = nextCols;
    itemWidth = nextItemWidth;
  }

  return cols;
}

/**
 * 固定卡片宽度时的 Masonry 列数：`floor((container + gap) / (cardWidth + gap))`，至少 1 列。
 * 与 `masonryColumnCountForWidth` 在 min=max 时的目标不同：后者在「加列会变窄过下限」时会保留偏宽的列宽。
 */
export function masonryColumnCountForFixedCardWidth(
  containerWidthPx: number,
  cardWidthPx: number,
): number {
  const w = Math.max(0, containerWidthPx);
  if (w <= 0) return 1;
  const gap = MASONRY_GAP_PX;
  const cw = Math.max(1, cardWidthPx);
  return Math.max(1, Math.floor((w + gap) / (cw + gap)));
}

/**
 * 从资源 URL 的 pathname 解析文件扩展名（小写，不含点）。
 * 会忽略路径最后一段中 `!` 之后的美图 CDN 处理参数（如 `.jpg!watermark-...0.07`），避免把 `07` 误当成扩展名。
 *
 * @param url 资源地址
 */
export function getExtensionFromAssetUrl(url: string): string | null {
  if (!url?.trim()) return null;
  try {
    const pathname = decodeURIComponent(new URL(url).pathname);
    const segments = pathname.split('/').filter(Boolean);
    const lastSegment = segments.pop() ?? '';
    const nameBeforeBang = lastSegment.split('!')[0] ?? lastSegment;
    const m = /\.([a-z0-9]+)$/i.exec(nameBeforeBang);
    return m ? m[1].toLowerCase() : null;
  } catch {
    return null;
  }
}

/**
 * 当 URL 无法解析后缀时，按 material_type 给出占位扩展名（仅用于展示分类）。
 *
 * @param materialType 接口下发的 material_type
 */
function inferExtensionFromMaterialType(materialType: string): string {
  switch ((materialType || '').toLowerCase()) {
    case 'image':
      return 'png';
    case 'video':
      return 'mp4';
    case 'audio':
      return 'mp3';
    case 'document':
      return 'pdf';
    case 'script':
      return 'md';
    case 'excel':
      return 'xlsx';
    case 'ppt':
      return 'ppt';
    default:
      return '';
  }
}

/**
 * 归一化展示用扩展名并合并 URL 与 material_type 信息。
 *
 * @param url 资源 URL
 * @param materialType material_type
 */
export function resolveAssetFileExtension(url: string, materialType: string): string {
  return getExtensionFromAssetUrl(url) ?? inferExtensionFromMaterialType(materialType);
}

/**
 * 判定资产卡片应采用 Figma「文档预览」布局（图标 + 双行文案）而非媒体缩略图。
 *
 * @param cardVariant 卡片类型
 * @param materialType material_type
 */
export function isDocumentCardLayout(cardVariant: AssetCardVariant, materialType: string): boolean {
  if (['ppt', 'excel', 'pdf', 'word', 'markdown', 'text', 'csv'].includes(cardVariant)) {
    return true;
  }
  const m = (materialType || '').toLowerCase();
  return cardVariant === 'unknown' && (m === 'document' || m === 'excel' || m === 'ppt');
}

/**
 * 由扩展名与 material_type 得到卡片展示变体。
 *
 * @param ext 小写扩展名，可能为空
 * @param materialType material_type
 */
export function getAssetCardVariant(ext: string, materialType: string): AssetCardVariant {
  const e = ext.toLowerCase();
  if (e && IMAGE_EXT.has(e)) return 'image';
  if (e && VIDEO_EXT.has(e)) return 'video';
  if (e && AUDIO_EXT.has(e)) return 'audio';
  if (e && PPT_EXT.has(e)) return 'ppt';
  if (e && EXCEL_EXT.has(e)) return 'excel';
  if (e === 'pdf') return 'pdf';
  if (e && WORD_EXT.has(e)) return 'word';
  if (e === 'md') return 'markdown';
  if (e === 'csv') return 'csv';
  if (e === 'txt') return 'text';

  const m = (materialType || '').toLowerCase();
  if (m === 'ppt') return 'ppt';
  if (m === 'excel') return 'excel';
  if (m === 'document') return 'unknown';
  if (m === 'image') return 'image';
  if (m === 'video') return 'video';
  if (m === 'audio') return 'audio';
  return 'unknown';
}

/**
 * 文档类卡片图标、底色与副标题类型文案（对齐 Figma 33703:123959）。
 *
 * @param variant 卡片变体
 */
export function getDocumentCardVisual(variant: AssetCardVariant): DocumentCardVisual {
  switch (variant) {
    case 'ppt':
      return { icon: 'PPTFileBold', iconBg: '#f59e66', typeLabel: 'PowerPoint' };
    case 'excel':
      return { icon: 'excelFileBold', iconBg: '#61cca2', typeLabel: 'Excel' };
    case 'pdf':
      return { icon: 'PDFFileBold', iconBg: '#f87171', typeLabel: 'PDF' };
    case 'word':
      return { icon: 'fileBold', iconBg: '#60a5fa', typeLabel: 'Word' };
    case 'markdown':
      return { icon: 'fileBold', iconBg: '#94a3b8', typeLabel: 'Markdown' };
    case 'csv':
      return { icon: 'fileBold', iconBg: '#94a3b8', typeLabel: 'CSV' };
    case 'text':
      return { icon: 'fileBold', iconBg: '#94a3b8', typeLabel: '文本' };
    default:
      return { icon: 'fileBold', iconBg: '#94a3b8', typeLabel: '文档' };
  }
}

/**
 * 从 URL 取展示文件名（含扩展名）；失败时用 asset id 占位。
 *
 * @param url 资源 URL
 * @param id 资产 id
 */
function displayFileNameFromAssetUrl(url: string, id: string): string {
  if (!url?.trim()) return `asset-${id}`;
  try {
    const path = decodeURIComponent(new URL(url).pathname);
    const seg = path.split('/').pop() || '';
    return seg || `asset-${id}`;
  } catch {
    return `asset-${id}`;
  }
}

/**
 * 解析列表项展示标题：优先接口 `name`，其次 `file_name`，否则由 URL 推断。
 *
 * @param raw 接口行
 * @param url 资源 URL
 * @param id 资产 id 字符串
 */
export function assetDisplayTitle(raw: AssetItem, url: string, id: string): string {
  const byName = raw.name?.trim();
  if (byName) return byName;
  const n = raw.file_name?.trim();
  if (n) return n;
  return displayFileNameFromAssetUrl(url, id);
}

/**
 * 将接口下发的字节数格式化为设计稿「1.68MB」形式（数字与单位紧贴）；优先 `file.size`，其次 `file_size`、顶层 `size`。无有效数据时返回 null。
 *
 * @param raw 接口行
 */
export function formatAssetByteSizeMeta(raw: AssetItem): string | null {
  const sized = raw as AssetItem & { file_size?: number; size?: number; };
  const bytes = sized.file?.size ?? sized.file_size ?? sized.size;
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes < 1024) return `${bytes}B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb >= 10 ? kb.toFixed(0) : kb.toFixed(1)}KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb >= 10 ? mb.toFixed(0) : mb.toFixed(2)}MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)}GB`;
}

/**
 * 文档卡用于取图标/颜色的变体键；后缀缺失时按 material_type 回退（如 excel / ppt）。
 *
 * @param cardVariant 卡片变体
 * @param materialType material_type
 */
export function documentVisualVariantKey(
  cardVariant: AssetCardVariant,
  materialType: string,
): AssetCardVariant {
  if (['ppt', 'excel', 'pdf', 'word', 'markdown', 'text', 'csv'].includes(cardVariant)) {
    return cardVariant;
  }
  const m = (materialType || '').toLowerCase();
  if (m === 'excel') return 'excel';
  if (m === 'ppt') return 'ppt';
  return 'unknown';
}
